import React, { useState } from 'react';
import {
  Box, Card, Typography, Tabs, Tab, Grid, FormControl, InputLabel,
  Select, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Avatar, CircularProgress, Alert, Paper, Divider
} from '@mui/material';
import {
  CalendarToday, AccessTime, Work, Person, CheckCircle,
  Cancel, HourglassEmpty, WorkHistory
} from '@mui/icons-material';

function fmtDate(date) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function statusColor(s) {
  if (!s) return 'default';
  switch (s.toLowerCase()) {
    case 'approved': return 'success';
    case 'pending': return 'warning';
    case 'rejected': return 'error';
    default: return 'default';
  }
}

// Build role × contentType breakdown from work entries
function buildRoleBreakdown(empWork) {
  const map = {};
  empWork.forEach(w => {
    const roles = w.myRole ? w.myRole.split(',').map(r => r.trim()) : ['Unknown'];
    const ct = (w.contentType || 'Long Video').toLowerCase();
    const isShort = ct === 'short' || ct === 'shorts' || ct === 'reel' || ct === 'reels' || ct === 'shorts video' || ct.startsWith('short');
    roles.forEach(role => {
      if (!role) return;
      if (!map[role]) map[role] = { long: 0, shorts: 0 };
      if (isShort) map[role].shorts += 1;
      else map[role].long += 1;
    });
  });
  return Object.entries(map)
    .map(([role, counts]) => ({ role, long: counts.long, shorts: counts.shorts, total: counts.long + counts.shorts }))
    .sort((a, b) => b.total - a.total);
}

function EmployeeOverviewTab({
  employees, overviewEmployeeId, setOverviewEmployeeId, selectedEmployee,
  empLeaves, empPermissions, empWork, overviewCompOff, overviewLoading,
  empTotalLeaveDays, empTotalPermHours, empCompOffDays,
  empPendingLeaves, empPendingPerms, periodLabel
}) {
  const [innerTab, setInnerTab] = useState(0);

  const empApprovedLeaves = empLeaves.filter(l => l.status === 'approved');
  const empRejectedLeaves = empLeaves.filter(l => l.status === 'rejected');
  const empPendingCompOff = overviewCompOff.filter(c => c.status === 'pending').length;
  const roleBreakdown = buildRoleBreakdown(empWork);
  // Sum of all role counts
  const totalLong = roleBreakdown.reduce((s, r) => s + r.long, 0);
  const totalShorts = roleBreakdown.reduce((s, r) => s + r.shorts, 0);
  // Count unique content items by type
  const uniqueLong = empWork.filter(w => {
    const ct = (w.contentType || 'Long Video').toLowerCase();
    return !ct.startsWith('short') && ct !== 'reel' && ct !== 'reels';
  }).length;
  const uniqueShorts = empWork.filter(w => {
    const ct = (w.contentType || '').toLowerCase();
    return ct.startsWith('short') || ct === 'reel' || ct === 'reels';
  }).length;

  return (
    <Box>
      {/* Employee Selector */}
      <Box mb={3}>
        <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
          <InputLabel>Select Employee</InputLabel>
          <Select
            value={overviewEmployeeId}
            onChange={(e) => { setOverviewEmployeeId(e.target.value); setInnerTab(0); }}
            label="Select Employee"
          >
            <MenuItem value=""><em>— Choose an employee —</em></MenuItem>
            {employees.map(emp => (
              <MenuItem key={emp.id} value={emp.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                    {emp.fullName?.charAt(0)}
                  </Avatar>
                  {emp.fullName}
                  <Typography variant="caption" color="text.secondary" ml={0.5}>({emp.role})</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!overviewEmployeeId && (
        <Alert severity="info" icon={<Person />}>
          Select an employee from the dropdown above to view their complete overview.
        </Alert>
      )}

      {overviewEmployeeId && (
        <>
          {/* Profile Header */}
          <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.3)', fontSize: '1.5rem', fontWeight: 700 }}>
                {selectedEmployee?.fullName?.charAt(0) || '?'}
              </Avatar>
              <Box flexGrow={1}>
                <Typography variant="h6" fontWeight={700}>{selectedEmployee?.fullName}</Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                  <Chip label={selectedEmployee?.role} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
                  {selectedEmployee?.employeeId && (
                    <Chip label={`ID: ${selectedEmployee.employeeId}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
                  )}
                  {selectedEmployee?.designation && (
                    <Chip label={selectedEmployee.designation} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
                  )}
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Period: {periodLabel}</Typography>
            </Box>
          </Paper>

          {/* Summary Cards — 4 cards only (removed Work Hours & Pending) */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', p: 1.5, border: '1px solid', borderColor: 'primary.light' }}>
                <CalendarToday color="primary" />
                <Typography variant="h5" fontWeight={700} color="primary">{empTotalLeaveDays.toFixed(1)}</Typography>
                <Typography variant="caption" color="text.secondary">Leave Days</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', p: 1.5, border: '1px solid', borderColor: 'info.light' }}>
                <AccessTime color="info" />
                <Typography variant="h5" fontWeight={700} color="info.main">{empTotalPermHours.toFixed(1)}</Typography>
                <Typography variant="caption" color="text.secondary">Perm. Hours</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', p: 1.5, border: '1px solid', borderColor: 'success.light' }}>
                <WorkHistory color="success" />
                <Typography variant="h5" fontWeight={700} color="success.main">{empWork.length}</Typography>
                <Typography variant="caption" color="text.secondary">Work Logs</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', p: 1.5, border: '1px solid', borderColor: '#ce93d8' }}>
                <CalendarToday sx={{ color: '#7b1fa2' }} />
                <Typography variant="h5" fontWeight={700} sx={{ color: '#7b1fa2' }}>{empCompOffDays}</Typography>
                <Typography variant="caption" color="text.secondary">Comp-Off Days</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Inner Tabs */}
          {overviewLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : (
            <Box>
              <Tabs value={innerTab} onChange={(e, v) => setInnerTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label={`Work Logs (${empWork.length})`} />
                <Tab label={`Leaves (${empLeaves.length})`} />
                <Tab label={`Permissions (${empPermissions.length})`} />
                <Tab label={`Comp-Off (${overviewCompOff.length})`} />
              </Tabs>

              {/* Work Logs — Role × Long/Shorts breakdown */}
              {innerTab === 0 && (
                <Box>
                  {empWork.length === 0 ? (
                    <Alert severity="info">No work logs for this period.</Alert>
                  ) : (
                    <>
                      {/* Role Breakdown Summary Table */}
                      <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                        ROLE BREAKDOWN
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>Long</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, color: 'secondary.main' }}>Shorts</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {roleBreakdown.map(row => (
                              <TableRow key={row.role} hover>
                                <TableCell>
                                  <Chip label={row.role} size="small" variant="outlined" color="default" />
                                </TableCell>
                                <TableCell align="center">
                                  {row.long > 0 ? (
                                    <Chip label={row.long} size="small" color="primary" sx={{ minWidth: 36 }} />
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  {row.shorts > 0 ? (
                                    <Chip label={row.shorts} size="small" color="secondary" sx={{ minWidth: 36 }} />
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip label={row.total} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36 }} />
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Total row — sum of all role counts */}
                            <TableRow sx={{ bgcolor: 'grey.100', borderTop: '2px solid', borderColor: 'divider' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Total (Role-wise)</TableCell>
                              <TableCell align="center">
                                <Chip label={totalLong} size="small" color="primary" sx={{ minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={totalShorts} size="small" color="secondary" sx={{ minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={totalLong + totalShorts} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                            </TableRow>
                            {/* Unique total row — unique content items by type */}
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Total (Unique Content)</TableCell>
                              <TableCell align="center">
                                <Chip label={uniqueLong} size="small" color="primary" variant="outlined" sx={{ minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={uniqueShorts} size="small" color="secondary" variant="outlined" sx={{ minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={empWork.length} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36, fontWeight: 700 }} />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Divider sx={{ mb: 2 }} />

                      {/* Detailed Work Log Table */}
                      <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                        DETAILED LOG
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell>Date</TableCell>
                              <TableCell>Content / Project</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                              <TableCell>Hours</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {empWork.map(w => (
                              <TableRow key={w.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(w.shootDate)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                    {w.content}
                                    {w.contentId && (
                                      <Typography component="span" variant="caption" color="text.disabled" ml={0.5}>
                                        ({w.contentId})
                                      </Typography>
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={w.myRole || '-'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                  {w.contentType ? (
                                    <Chip
                                      label={w.contentType}
                                      size="small"
                                      color={['short', 'shorts', 'reel', 'reels', 'shorts video'].includes((w.contentType || '').toLowerCase()) ? 'secondary' : 'primary'}
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Chip label="Long" size="small" color="primary" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {w.hours != null
                                    ? <Chip label={`${w.hours}h`} size="small" color="success" />
                                    : <Typography variant="body2" color="text.disabled">—</Typography>
                                  }
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                  {w.source === 'content_library'
                                    ? <Chip label="Content Library" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                                    : <Chip label="Daily Work" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              )}

              {/* Leaves */}
              {innerTab === 1 && (
                <Box>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip icon={<CheckCircle />} label={`Approved: ${empApprovedLeaves.length} (${empApprovedLeaves.reduce((s, l) => s + (parseFloat(l.numberOfDays) || 0), 0).toFixed(1)} days)`} color="success" variant="outlined" size="small" />
                    <Chip icon={<HourglassEmpty />} label={`Pending: ${empPendingLeaves}`} color="warning" variant="outlined" size="small" />
                    <Chip icon={<Cancel />} label={`Rejected: ${empRejectedLeaves.length}`} color="error" variant="outlined" size="small" />
                  </Box>
                  {empLeaves.length === 0 ? <Alert severity="info">No leave requests for this period.</Alert> : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>Type</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell>Days</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {empLeaves.map(l => (
                            <TableRow key={l.id} hover>
                              <TableCell>{l.leaveType}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(l.startDate)} → {fmtDate(l.endDate)}</TableCell>
                              <TableCell><Chip label={l.numberOfDays} size="small" color="primary" /></TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{l.reason}</Typography>
                              </TableCell>
                              <TableCell><Chip label={l.status} size="small" color={statusColor(l.status)} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Permissions */}
              {innerTab === 2 && (
                <Box>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip icon={<CheckCircle />} label={`Approved: ${empPermissions.filter(p => p.status === 'approved').length}`} color="success" variant="outlined" size="small" />
                    <Chip icon={<HourglassEmpty />} label={`Pending: ${empPendingPerms}`} color="warning" variant="outlined" size="small" />
                    <Chip label={`Total: ${empTotalPermHours.toFixed(1)} hrs`} color="info" variant="outlined" size="small" />
                  </Box>
                  {empPermissions.length === 0 ? <Alert severity="info">No permission requests for this period.</Alert> : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>Date</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Time</TableCell>
                            <TableCell>Hours</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {empPermissions.map(p => (
                            <TableRow key={p.id} hover>
                              <TableCell>{fmtDate(p.permissionDate)}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{p.startTime} – {p.endTime}</TableCell>
                              <TableCell><Chip label={`${p.hours}h`} size="small" color="info" /></TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{p.reason}</Typography>
                              </TableCell>
                              <TableCell><Chip label={p.status} size="small" color={statusColor(p.status)} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Comp-Off */}
              {innerTab === 3 && (
                <Box>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip icon={<CheckCircle />} label={`Approved: ${overviewCompOff.filter(c => c.status === 'approved').length} (${empCompOffDays} days)`} color="success" variant="outlined" size="small" />
                    <Chip icon={<HourglassEmpty />} label={`Pending: ${empPendingCompOff}`} color="warning" variant="outlined" size="small" />
                    <Chip icon={<Cancel />} label={`Rejected: ${overviewCompOff.filter(c => c.status === 'rejected').length}`} color="error" variant="outlined" size="small" />
                  </Box>
                  {overviewCompOff.length === 0 ? <Alert severity="info">No comp-off claims found.</Alert> : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>Work Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Settlement</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Expiry</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewCompOff.map(c => (
                            <TableRow key={c.id} hover>
                              <TableCell>{fmtDate(c.workDate)}</TableCell>
                              <TableCell>
                                <Chip label={c.isHalfDay ? 'Half Day' : 'Full Day'} size="small" color={c.isHalfDay ? 'warning' : 'primary'} variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{c.reason}</Typography>
                              </TableCell>
                              <TableCell><Chip label={c.status} size="small" color={statusColor(c.status)} /></TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                {c.status === 'approved' && (
                                  c.settlementType === 'paid'
                                    ? <Chip label="Paid" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
                                    : c.settlementType === 'leave_taken'
                                    ? <Chip label="Leave Taken" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                                    : <Chip label="Not Settled" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />
                                )}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{c.expiryDate ? fmtDate(c.expiryDate) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default EmployeeOverviewTab;